import {consumptionGatewayInterface} from "../../../corelogic/usecases/consumption/consumptionGatewayInterface";
import {Weather} from "../../../corelogic/models/entities/consumption/weather";
import {Ecoconseil} from "../../../corelogic/models/entities/consumption/ecoconseil";
import client from "../libs/client";
import axios from "axios";
import {
    JOURNEE,
    JOURNEES,
    WEATHER_CLEAR,
    WEATHER_CLOUDY,
    WEATHER_MISTY,
    WEATHER_RAINY,
    WEATHER_SNOWY,
    WEATHER_SUNNY,
    WEATHER_THUNDER
} from "../../../config/app-config";
import {DailyWeather} from "../../../corelogic/models/entities/consumption/dailyWeather";
import {Coordinates} from "../../../corelogic/models/entities/consumption/coordinates";
import {t} from "i18next";

function weatherTypeENtoFR(weatherEnglish: string): string {
    const WEATHER_MAPPING: { [key: string]: string } = {
        "Clouds": WEATHER_CLOUDY,
        "Rain": WEATHER_RAINY,
        "Drizzle": WEATHER_RAINY,
        "Clear": WEATHER_CLEAR,
        "Thunderstorm": WEATHER_THUNDER,
        "Snow": WEATHER_SNOWY,
        "Mist": WEATHER_MISTY,
        "Smoke": WEATHER_MISTY,
        "Haze": WEATHER_MISTY,
        "Dust": WEATHER_MISTY,
        "Fog": WEATHER_MISTY,
        "Sand": WEATHER_MISTY,
        "Ash": WEATHER_MISTY,
        "Squall": WEATHER_MISTY,
        "Tornado": WEATHER_MISTY
    };

    return WEATHER_MAPPING[weatherEnglish] || WEATHER_SUNNY;
}

export default class APIConsumptionGateway implements consumptionGatewayInterface {
    error: any;

    getProductionTypes = async (operationId: number): Promise<string[]> => {
        const {data: typesOfProduction} = await client.get(`/operations/${operationId}/enedis-details/production-types`);
        return typesOfProduction;
    };

    getCoordinates = async (city: string, postCode: string): Promise<Coordinates | null> => {
        const url_geo = `https://api-adresse.data.gouv.fr/search/?q=${city}&postcode=${postCode}&limit=1`;

        try {
            const geoResponse = await axios.get(url_geo);
            if (geoResponse.data.features.length === 0) {
                return null;
            }
            const [longitude, latitude] = geoResponse.data.features[0].geometry.coordinates;
            return new Coordinates(longitude, latitude);
        } catch (error: any) {
            console.error("Erreur de géolocalisation:", error);

            return null;
        }
    };

    getWeather = async (operationId: number, city: string, coordinates: Coordinates): Promise<DailyWeather[]> => {
        const {latitude, longitude} = coordinates;
        const baseParams = `lat=${latitude}&lon=${longitude}&units=metric&appid=${process.env.REACT_APP_WEATHER_API_ID}&lang=fr`;

        try {
            const forecastResponse = await axios.get(`${process.env.REACT_APP_WEATHER_API_URL}?${baseParams}`);

            const today = new Date();
            const todayForecast = forecastResponse.data.list.filter((f: any) => {
                const entryDate = new Date(f.dt * 1000);
                return entryDate.toDateString() === today.toDateString();
            });

            const currentWeather = todayForecast[todayForecast.length - 1];

            const todayWeather = this.processForecastPeriods(
                [currentWeather, ...todayForecast],
                city
            );
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            const tomorrowForecast = forecastResponse.data.list.filter((f: any) => {
                const entryDate = new Date(f.dt * 1000);
                return entryDate.toDateString() === tomorrow.toDateString();
            });

            return [
                new DailyWeather(JOURNEES(t).TODAY, todayWeather),
                new DailyWeather(JOURNEES(t).TOMORROW, this.processForecastPeriods(tomorrowForecast, city))
            ];

        } catch (error: any) {
            console.error("Erreur météo:", error);
            return [
                new DailyWeather(JOURNEES(t).TODAY, []),
                new DailyWeather(JOURNEES(t).TOMORROW, [])
            ];
        }
    };

    private processForecastPeriods(data: any[], city: string): Weather[] {
        const periodBoundaries = {
            [JOURNEE(t).MORNING]: [6, 10],
            [JOURNEE(t).MIDDAY]: [10, 17],
            [JOURNEE(t).EVENING]: [17, 22]
        };

        return Object.entries(periodBoundaries).map(([period, [start, end]]) => {
            const periodData = data
                .filter(entry => {
                    const timeStampInMs = entry.dt * 1000;
                    const localDate = new Date(timeStampInMs);
                    const localHour = localDate.getUTCHours() + (localDate.getTimezoneOffset() / 60);
                    return localHour >= start && localHour < end;
                })
                .sort((a, b) => b.dt - a.dt);

            const weatherCounts = periodData.reduce((acc, curr) => {
                const type = curr.weather[0].main;
                acc[type] = (acc[type] || 0) + 1;
                return acc;
            }, {} as Record<string, number>);

            const dominantWeather = Object.entries(weatherCounts)
                .sort((a, b) => (b[1] as number) - (a[1] as number))
                [0]?.[0] || 'Clear';


            return new Weather(
                weatherTypeENtoFR(dominantWeather),
                city,
                period,
                periodData.length > 0
            );
        });
    }

    getEcoConseils = async (): Promise<Ecoconseil[]> => {
        const {data: advices} = await client.get(`/advices/`);
        return advices.map((advice: { category: string, title: string, description: string, pros: string }) => {
            return new Ecoconseil(advice.category, advice.title, advice.description, advice.pros);
        });
    };
}
